import type { Ref } from 'vue';
import { ref, computed, set, watch } from 'vue';
import type { GlobalStore } from 'src/stores/global-store';
import type { UserEntryViewMeta } from '@cognitoforms/types/server-types/core/services/user-entry-view-meta';
import type { UserFormMeta } from '@cognitoforms/types/server-types/core/services/user-form-meta';
import type { UserInfo } from '@cognitoforms/types/server-types/forms/model/user-info';
import type { FormEntryIndex } from '@cognitoforms/types/server-types/forms/model/form-entry-index';
import type { FormEntry } from '@cognitoforms/types/server-types/forms/model/form-entry';
import { getViewId, getEntryId, getRoleInfo, getViewName, getFormName, getEntryKey, getViewToken, getFormTitle } from './use-task-utils';
import type { AdminApi } from '../../../../../client/src/embed-scripts/admin';
import type { FormLightboxOptions } from './use-form-lightbox';
import { showConfirmationMessage } from './confirmation-messages';

type UseTaskManagementOptions = {
	globalStore: GlobalStore;
	TASK_LIMIT: number;
	firstDayOfWeek: string;
	adminApi: Ref<AdminApi>;
	openFormLightbox: FormLightboxOptions['openFormLightbox'];
	closeFormLightbox: FormLightboxOptions['closeFormLightbox'];
}

export enum GroupingOptions {
	DueDate = 'Due Date',
	View = 'Type',
	Priority = 'Priority',
};

export enum TaskOrganizationPriority {
	High = '1 - High',
	Medium = '2 - Medium',
	Low = '3 - Low',
	Unprioritized = 'Unprioritized'
};

export enum TaskOrganizationDate {
	Unscheduled = 'Unscheduled',
	PastDue = 'Past Due',
	Today = 'Today',
	Tomorrow = 'Tomorrow',
	ThisWeek = 'This Week',
	NextWeek = 'Next Week',
	Later = 'Later'
};

// Used to determine the last day of the week of 'startDate' dependent upon
// when the Org's culture defines the 'firstDayOfWeek' (ie UK = Monday, US = Sunday)
export function getEndOfWeek(startDate: Date, firstDayOfWeek: string) {
	const firstDayIndex = Number(firstDayOfWeek);

	const startOfWeek = new Date(startDate);
	const offset = (startDate.getUTCDay() - firstDayIndex + 7) % 7;

	// Adjust the startOfWeek to the firstDayOfWeek
	startOfWeek.setUTCDate(startDate.getUTCDate() - offset);

	const endOfWeek = new Date(startOfWeek);
	endOfWeek.setUTCDate(startOfWeek.getUTCDate() + 6); // End of this week

	return endOfWeek;
};

export function useTaskManagement(options: UseTaskManagementOptions) {
	const taskViews = computed<UserEntryViewMeta[]>(() => {
		if (!options.globalStore.forms)
			return [];
		return [...options.globalStore.taskViews];
	});

	const tasks = ref([] as FormEntryIndex[]);

	const userInfo = computed<UserInfo>(() => {
		if (!options.globalStore.user)
			return;

		return {
			Name: options.globalStore.fullName,
			FirstName: options.globalStore.user.FirstName,
			LastName: options.globalStore.user.LastName,
			Email: options.globalStore.user.Email
		} as UserInfo;
	});

	const entrySets = ref([]);
	const taskCount = ref(0);
	const loadTasksForViews = async (viewIds : string[], isInitialLoad : boolean = false) => {
		const taskViewsOrderedByTaskCountAsc = taskViews.value.filter(taskView => viewIds.includes(taskView.Id)).sort((a, b) => a.Entries - b.Entries);
		const loadIndexesRequests = [];

		for (let i = 0; i < taskViewsOrderedByTaskCountAsc.length; i++) {
			if (taskCount.value >= options.TASK_LIMIT)
				break;

			const taskView = taskViewsOrderedByTaskCountAsc[i];
			const remainingCapacity = Math.max(0, options.TASK_LIMIT - taskCount.value);
			taskCount.value += Math.min(taskView.Entries, remainingCapacity);

			const fetchIndexesRequest = async () => {
				let entrySet = entrySets.value.find(entrySet => entrySet.view === taskView.Id);
				if (!entrySet) {
					const formId = taskView.Id.split('-')[0];
					const userShort = await options.adminApi.value.getUserShort(formId);
					const isUserSpecific = taskView.IsUserSpecific;
					entrySet = await options.adminApi.value.getEntrySet(taskView.Id, taskView.Token, getRoleInfo(taskView), userInfo.value, isUserSpecific ? userShort : undefined);
					entrySets.value.push(entrySet);
				}

				const fetchedIndexes = [] as FormEntryIndex[];
				const shouldRefreshEntrySet = !isInitialLoad || !taskView.LastUpdated || new Date(taskView.LastUpdated) > new Date(entrySet.timestamp?.length > 0 ? entrySet.timestamp : null);
				if (shouldRefreshEntrySet) {
					await entrySet.refresh();
					fetchedIndexes.push(...entrySet.listIndex());
				}
				else
					fetchedIndexes.push(...entrySet.indexes.values());

				const indexes = [] as FormEntryIndex[];
				if (fetchedIndexes.length <= remainingCapacity)
					indexes.push(...fetchedIndexes);
				else
					indexes.push(...fetchedIndexes.slice(0, remainingCapacity));

				// Remove existing indexes no longer present in the entry set
				const oldIndexes = tasks.value.filter(task => getViewId(task) === entrySet.view);
				const indexesToRemove = oldIndexes.filter(oldIndex => !fetchedIndexes.some(newIndex => getEntryId(newIndex) === getEntryId(oldIndex)));
				indexesToRemove.forEach(indexToRemove => {
					const taskIndex = tasks.value.findIndex(task => getEntryId(task) === getEntryId(indexToRemove) && getViewId(task) === getViewId(indexToRemove));
					tasks.value.splice(taskIndex, 1);
				});

				// Replace existing index if refetched, else push new index
				indexes.forEach(newIndex => {
					const existingTaskIndex = tasks.value.findIndex(task => {
						const isSameView = getViewId(task) === getViewId(newIndex);
						const isSameEntry = getEntryId(task) === getEntryId(newIndex);
						return isSameView && isSameEntry;
					});

					if (existingTaskIndex === -1)
						tasks.value.push(newIndex);
					else
						set(tasks.value, existingTaskIndex, newIndex);
				});
			};
			loadIndexesRequests.push(() => fetchIndexesRequest());
		}

		await Promise.all(loadIndexesRequests.map(request => request()));
	};

	const removeTasksFromViews = (taskViewIds: string[]) => {
		tasks.value = tasks.value.filter(task => !taskViewIds.includes(getViewId(task)));
	};

	watch(() => options.globalStore.forms, (newVal, oldVal) => {
		const restoredTaskViewIds = Object.values(newVal).filter(newForm => !newForm.IsArchived && oldVal[newForm.Id]?.IsArchived).flatMap(f => f.EntryViews.filter(v => v.IsTaskView && v.Entries > 0)).map(view => view.Id);
		if (restoredTaskViewIds.length > 0)
			loadTasksForViews(restoredTaskViewIds);
	});

	watch(() => taskViews.value, (newVal, oldVal) => {
		const removedTaskViewIds = oldVal.filter(oldTaskView => !newVal.some(newTaskView => newTaskView.Id === oldTaskView.Id)).map(view => view.Id);
		if (removedTaskViewIds.length > 0)
			removeTasksFromViews(removedTaskViewIds);

		const restoredTaskViewIds = newVal.filter(newTaskView => !oldVal.some(oldTaskView => oldTaskView.Id === newTaskView.Id)).map(view => view.Id);
		if (restoredTaskViewIds.length > 0)
			loadTasksForViews(restoredTaskViewIds);
	});

	const formsWithTaskViews = computed<UserFormMeta[]>(() => {
		if (!options.globalStore.forms)
			return [];
		return [...options.globalStore.formsWithTaskViews];
	});

	const getTaskPriority = (identifier : string | FormEntryIndex) : number | null => {
		const taskViewId = typeof identifier === 'string' ? identifier : getViewId(identifier);

		const taskView = taskViews.value.find(taskView => taskView.Id === taskViewId);
		if (!taskView)
			return null;

		return taskView.TaskPriority;
	};

	const getTaskDueDate = (identifier: string | FormEntryIndex): Date | null => {
		if (typeof identifier !== 'string' && identifier.DueDate)
			return identifier.DueDate;

		const taskViewId = typeof identifier === 'string'
			? identifier
			: getViewId(identifier);

		const taskView = taskViews.value.find(taskView => taskView.Id === taskViewId);
		if (taskView?.TaskDueDate)
			return taskView.TaskDueDate;

		return null;
	};

	const getStaticDueDate = (index: FormEntryIndex): Date | null => {
		const taskView = taskViews.value.find(taskView => taskView.Id === getViewId(index));
		return taskView?.TaskDueDate;
	};

	const priorityMap = {
		'1': TaskOrganizationPriority.High,
		'2': TaskOrganizationPriority.Medium,
		'3': TaskOrganizationPriority.Low
	};

	const isSameDay = (date1: Date, date2: Date) => {
		return (
			date1.getFullYear() === date2.getFullYear() &&
			date1.getMonth() === date2.getMonth() &&
			date1.getDate() === date2.getDate()
		);
	};

	const getDatePriorityKey = (taskDueDate : Date): TaskOrganizationDate => {
		if (!taskDueDate) return TaskOrganizationDate.Unscheduled;

		const now = new Date();
		const today = new Date(now.setHours(0, 0, 0, 0)); // Midnight of today
		const tomorrow = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);

		const taskDate = new Date(taskDueDate);

		if (taskDate < today) return TaskOrganizationDate.PastDue; // Earlier than today
		if (isSameDay(taskDate, today)) return TaskOrganizationDate.Today;
		if (isSameDay(taskDate, tomorrow)) return TaskOrganizationDate.Tomorrow;

		// This week range
		const endOfWeek = getEndOfWeek(today, options.firstDayOfWeek);
		if (taskDate <= endOfWeek) return TaskOrganizationDate.ThisWeek;

		// Next week range
		const endOfNextWeek = getEndOfWeek(new Date(endOfWeek.getFullYear(), endOfWeek.getMonth(), endOfWeek.getDate() + 1), options.firstDayOfWeek);
		if (taskDate <= endOfNextWeek) return TaskOrganizationDate.NextWeek;

		return TaskOrganizationDate.Later;
	};

	const tasksSortedByView = computed<Map<string, FormEntryIndex[]>>(() => {
		const sortedTasks = new Map();
		if (tasks.value.length === 0)
			return sortedTasks;

		const taskViewObjects = Array.from(new Set<string>(tasks.value.map(index => getViewId(index))))
			.map(taskViewId => {
				const task = tasks.value.find(task => getViewId(task) === taskViewId);
				return {
					taskViewId,
					taskViewName: getViewName(task, taskViews.value),
					formName: getFormName(task, options.globalStore)
				};
			});

		const taskViewIdsSortedAlphabetically = taskViewObjects
			.sort((a, b) => {
				const formNameComparison = a.formName.localeCompare(b.formName);
				if (formNameComparison !== 0) {
					return formNameComparison;
				}
				return a.taskViewName.localeCompare(b.taskViewName);
			})
			.map(item => item.taskViewId);

		return taskViewIdsSortedAlphabetically.reduce((map, viewId) => {
			const tasksInView = tasks.value.filter(task => getViewId(task) === viewId);

			if (tasksInView.length > 0)
				map.set(viewId, tasksInView);

			return map;
		}, new Map<string, FormEntryIndex[]>());
	});

	const tasksSortedByPriority = computed<Map<TaskOrganizationPriority, FormEntryIndex[]>>(() => {
		const sortedTasks = new Map();
		if (tasks.value.length === 0)
			return sortedTasks;

		const taskObjects = tasks.value.map(task => {
			const taskViewId = getViewId(task);
			return {
				taskIndex: task,
				taskViewId: taskViewId,
				taskViewName: getViewName(task, taskViews.value),
				formName: getFormName(task, options.globalStore),
				taskPriority: getTaskPriority(taskViewId),
				taskDate: getTaskDueDate(task) ?? getTaskDueDate(taskViewId)
			};
		});

		const sortedTaskViewObjects = taskObjects
			.sort((a, b) => {
				// Sort priority: ascending order with null treated as the highest
				if (a.taskPriority === null && b.taskPriority !== null) return -1;
				if (b.taskPriority === null && a.taskPriority !== null) return 1;
				if (a.taskPriority !== b.taskPriority) return a.taskPriority - b.taskPriority;

				const dateA = a.taskDate ? new Date(a.taskDate) : null;
				const dateB = b.taskDate ? new Date(b.taskDate) : null;

				// secondary date sort
				if (dateA === null && dateB !== null) return 1;
				if (dateB === null && dateA !== null) return -1;
				if (dateA && dateB) {
					const dateComparison = dateA.getTime() - dateB.getTime();
					if (dateComparison !== 0) return dateComparison;
				}

				// tertiary formName sort
				const formNameComparison = a.formName.localeCompare(b.formName);
				if (formNameComparison !== 0) return formNameComparison;

				// quaternary taskViewName sort
				return a.taskViewName.localeCompare(b.taskViewName);
			});

		return sortedTaskViewObjects.reduce((map, { taskPriority, taskIndex }) => {
			const priorityKey = priorityMap[taskPriority] || TaskOrganizationPriority.Unprioritized;

			if (!map.has(priorityKey))
				map.set(priorityKey, []);

			map.get(priorityKey).push(taskIndex);

			return map;
		}, new Map<TaskOrganizationPriority, FormEntryIndex[]>());
	});

	const tasksSortedByDate = computed<Map<TaskOrganizationDate, FormEntryIndex[]>>(() => {
		const sortedTasks = new Map();

		if (tasks.value.length === 0)
			return sortedTasks;

		const taskObjects = tasks.value.map(task => {
			const taskViewId = getViewId(task);
			return {
				taskIndex: task,
				taskViewName: getViewName(task, taskViews.value),
				formName: getFormName(task, options.globalStore),
				taskPriority: getTaskPriority(taskViewId),
				taskDate: getTaskDueDate(task) ?? getTaskDueDate(taskViewId)
			};
		});

		const sortedTaskObjects = taskObjects
			.sort((a, b) => {
				const dateA = a.taskDate ? new Date(a.taskDate) : null;
				const dateB = b.taskDate ? new Date(b.taskDate) : null;

				// Sort priority: ascending order with null treated as the highest
				if (dateA === null && dateB !== null) return -1;
				if (dateB === null && dateA !== null) return 1;
				if (dateA && dateB) {
					const dateComparison = dateA.getTime() - dateB.getTime();
					if (dateComparison !== 0) return dateComparison;
				}

				// secondary priority sort
				if (a.taskPriority === null && b.taskPriority !== null) return 1;
				if (b.taskPriority === null && a.taskPriority !== null) return -1;
				if (a.taskPriority !== b.taskPriority) return a.taskPriority - b.taskPriority;

				// tertiary formName sort
				const formNameComparison = a.formName.localeCompare(b.formName);
				if (formNameComparison !== 0) return formNameComparison;

				// quaternary taskViewName sort
				return a.taskViewName.localeCompare(b.taskViewName);
			});

		return sortedTaskObjects.reduce((map, task) => {
			const priorityKey = getDatePriorityKey(task.taskDate);
			if (!map.has(priorityKey)) {
				map.set(priorityKey, []);
			}
			map.get(priorityKey).push(task.taskIndex);

			return map;
		}, new Map<TaskOrganizationDate, FormEntryIndex[]>());
	});

	const getTaskSource = (index: FormEntryIndex) => {
		return getFormName(index, options.globalStore) + ' - ' + getViewName(index, taskViews.value);
	};

	const visibleTasks = new Set<string>();
	const getEntry = async (index : FormEntryIndex) : Promise<FormEntry> => {
		const entrySet = entrySets.value.find(entrySet => entrySet.view === getViewId(index));
		const entry = await entrySet?.getEntry(index);

		// track tasks so we know if we need to load additional tasks actions
		visibleTasks.add(index.Id);

		return entry as FormEntry;
	};

	const selectedEntry = ref(null);
	const showLightbox = ref(false);
	const selectedViewId = ref('');
	const taskRoleId = ref(null);
	const viewToken = ref('');
	const dialogTitle = ref('');
	const entryKey = ref(null);
	const showLocalizedError = ref(false);
	const inlineActionName = ref(null);

	const onLightboxClose = () => {
		selectedEntry.value = null;
		inlineActionName.value = null;
		showLocalizedError.value = false;
	};

	const onTaskClick = async (index : FormEntryIndex, showErrorMessage: boolean) => {
		selectedEntry.value = await getEntry(index);
		selectedViewId.value = getViewId(index);

		const taskView = taskViews.value.find(taskView => taskView.Id === selectedViewId.value);
		dialogTitle.value = getFormTitle(index, options.globalStore) + (index.Summary ? ' - ' + index.Summary : '');
		entryKey.value = getEntryKey(selectedEntry.value);
		taskRoleId.value = taskView.RoleId;
		viewToken.value = getViewToken(selectedViewId.value, taskViews);
		if (showErrorMessage)
			showLocalizedError.value = true;

		options.openFormLightbox(
			entryKey.value,
			selectedEntry.value.Form.Id,
			viewToken.value,
			taskView.Id,
			taskRoleId.value,
			dialogTitle.value,
			inlineActionName.value,
			showLocalizedError.value,
			(confirmationMessage: string) => {
				showConfirmationMessage(confirmationMessage);
				options.closeFormLightbox();
			},
			onLightboxClose
		);
	};

	return {
		tasks,
		taskViews,
		formsWithTaskViews,
		userInfo,
		loadTasksForViews,
		tasksSortedByView,
		tasksSortedByPriority,
		tasksSortedByDate,
		getTaskSource,
		getEntry,
		getTaskDueDate,
		getStaticDueDate,
		getTaskPriority,
		onTaskClick,
		onLightboxClose,
		selectedEntry,
		showLightbox,
		selectedViewId,
		taskRoleId,
		viewToken,
		dialogTitle,
		entryKey,
		inlineActionName,
		visibleTasks,
		showLocalizedError
	};
}